import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../pages/AuthContext';
import "../styles/Customer.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faUserPlus, 
    faUser, 
    faPhone, 
    faCalendar, 
    faMapMarkerAlt, 
    faSave, 
    faTimes, 
    faSpinner, 
    faCheckCircle, 
    faExclamationTriangle,
    faStore,
    faExchangeAlt,
    faTag,
    faChevronDown,
    faEnvelope,
    faUsers
} from '@fortawesome/free-solid-svg-icons';

// Create translator instance
const createCustomerTranslator = () => {
    let cache = new Map();
    let preloadedLanguages = new Set(['en', 'ur', 'ar', 'es']);

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Customer Form");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        addCustomer: 'Add Customer',
        fillDetails: 'Fill in the customer details below',
        customerName: 'Customer Name',
        storeName: 'Store Name',
        phoneNumber: 'Phone Number',
        email: 'Email',
        date: 'Date',
        address: 'Address',
        category: 'Category',
        selectCategory: 'Select a category',
        addCustomerButton: 'Save Customer',
        clearButton: 'Clear',
        adding: 'Adding...',
        customerAdded: 'Customer saved successfully!',
        failedAdd: 'Failed to save customer',
        networkError: 'Network error occurred',
        invalidPhone: 'Invalid phone number',
        invalidEmail: 'Invalid email address',
        today: 'Today',
        nameRequired: 'Customer name is required',
        nameTooShort: 'Name must be at least 2 characters',
        phoneInvalid: 'Please enter a valid phone number',
        emailInvalid: 'Please enter a valid email address',
        dateInvalid: 'Please select a valid date',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        selectShop: 'Select a shop first',
        currentShop: 'Current Shop',
        willAddToShop: 'Customer will be added to this shop',
        noShopSelected: 'No shop selected. Please switch to a shop first.',
        switchShop: 'Switch Shop',
        shopSwitched: 'Shop switched successfully',
        shopSwitchFailed: 'Failed to switch shop',
        noShopPermission: 'You do not have permission to add customers to this shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        active: 'Active',
        current: 'Current',
        optional: 'Optional',
        customerOf: 'Customer Of',
        assignToEmployee: 'Assign to Employee',
        searchEmployee: 'Search employee...',
        noEmployees: 'No employees available for this shop',
        noMatchingEmployees: 'No matching employees found',
        loadingEmployees: 'Loading employees...',
        regular: 'Regular',
        vip: 'VIP',
        corporate: 'Corporate',
        wholesale: 'Wholesale',
        retail: 'Retail',
        new: 'New',
        returning: 'Returning',
        premium: 'Premium'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const customerTranslator = createCustomerTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations.success || 'Success';
            case 'error':
                return translations.error || 'Error';
            case 'warning':
                return translations.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div 
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button 
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div 
                    className="toast-progress-bar"
                    style={{ 
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    const handleShopSelect = (shop) => {
        onSelectShop(shop);
        onClose();
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content shop-selector-modal">
                <div className="modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div 
                                    key={shop.shop_id}
                                    className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                    onClick={() => handleShopSelect(shop)}
                                >
                                    <div className="shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                        {shop.shop_role && (
                                            <div className="shop-item-role">Role: {shop.shop_role}</div>
                                        )}
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const AddCustomer = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [formData, setFormData] = useState({
        customer_name: '',
        store_name: '',
        phone: '',
        email: '',
        date: new Date().toISOString().split('T')[0],
        address: '',
        category: '',
        customer_of: '',
        themeColor: localStorage.getItem('ourzest_theme') || '#6366f1'
    });
    
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [translations, setTranslations] = useState({});
    
    // Employee state
    const [employees, setEmployees] = useState([]);
    const [loadingEmployees, setLoadingEmployees] = useState(false);
    const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
    const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
    
    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });
    
    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);
    
    // Category dropdown state
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [categorySearchTerm, setCategorySearchTerm] = useState('');
    
    // Refs for duplicate prevention
    const isSubmittingRef = useRef(false);
    const submissionLockRef = useRef(false);
    const lastSubmissionRef = useRef(null);
    const toastTimerRef = useRef(null);
    const formRef = useRef(null);
    const categoryDropdownRef = useRef(null);
    const employeeDropdownRef = useRef(null);
    const submitButtonRef = useRef(null);

    // Generate unique submission ID
    const generateSubmissionId = () => {
        return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    };

    // Category options
    const categoryOptions = [
        { value: 'regular', label: 'regular', displayName: 'Regular' },
        { value: 'vip', label: 'vip', displayName: 'VIP' },
        { value: 'corporate', label: 'corporate', displayName: 'Corporate' },
        { value: 'wholesale', label: 'wholesale', displayName: 'Wholesale' },
        { value: 'retail', label: 'retail', displayName: 'Retail' },
        { value: 'new', label: 'new', displayName: 'New' },
        { value: 'returning', label: 'returning', displayName: 'Returning' },
        { value: 'premium', label: 'premium', displayName: 'Premium' }
    ];

    // Filtered categories based on search
    const filteredCategories = categorySearchTerm.trim() === '' 
        ? categoryOptions 
        : categoryOptions.filter(cat => 
            cat.displayName.toLowerCase().includes(categorySearchTerm.toLowerCase()) ||
            cat.value.toLowerCase().includes(categorySearchTerm.toLowerCase())
          );

    // Filtered employees based on search
    const filteredEmployees = employeeSearchTerm.trim() === '' 
        ? employees 
        : employees.filter(emp => 
            emp.employee_name.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
            (emp.position && emp.position.toLowerCase().includes(employeeSearchTerm.toLowerCase()))
          );

    // Function to fetch employees for the current shop - USING customers.php
    const fetchEmployees = useCallback(async () => {
        if (!currentShop || !currentShop.shop_id) {
            console.log('No shop selected, skipping employee fetch');
            setEmployees([]);
            return;
        }
        
        setLoadingEmployees(true);
        try {
            console.log('Fetching employees for shop:', currentShop.shop_id);
            
            // Use customers.php with get_employees action
            const response = await fetch('https://ourzestapp.online/oz_backend/customers.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_employees',
                    shop_id: currentShop.shop_id
                })
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            console.log('Employees API Response from customers.php:', data);
            
            if (data.status === 'success' && data.data) {
                setEmployees(data.data);
                console.log(`Loaded ${data.data.length} employees`);
            } else {
                console.error('Failed to fetch employees:', data.message);
                setEmployees([]);
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
            setEmployees([]);
        } finally {
            setLoadingEmployees(false);
        }
    }, [currentShop]);

    // Handle click outside dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
                setShowCategoryDropdown(false);
            }
            if (employeeDropdownRef.current && !employeeDropdownRef.current.contains(event.target)) {
                setShowEmployeeDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Fetch employees when shop changes
    useEffect(() => {
        if (currentShop && currentShop.shop_id) {
            fetchEmployees();
            // Reset employee selection when shop changes
            setFormData(prev => ({ ...prev, customer_of: '' }));
            setEmployeeSearchTerm('');
        }
    }, [currentShop, fetchEmployees]);

    // Function to show toast
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }
        
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';
        
         setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 30
        });

        let timeLeft = 30;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 30) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    }, [currentLanguage]);

    // Clean up timers on component unmount
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
            isSubmittingRef.current = false;
            submissionLockRef.current = false;
        };
    }, []);

    // Main submit function with enhanced duplicate prevention
    const submitCustomer = useCallback(async (submitData, submissionId) => {
        // Double-check lock before proceeding
        if (isSubmittingRef.current || submissionLockRef.current) {
            console.log('🚫 Submission blocked - already in progress (double-check)');
            return false;
        }

        // Set locks
        isSubmittingRef.current = true;
        submissionLockRef.current = true;
        
        // Disable submit button visually
        if (submitButtonRef.current) {
            submitButtonRef.current.disabled = true;
        }

        try {
            const loggedInUsername = user?.username || 'Unknown';
            
            if (!loggedInUsername || loggedInUsername === 'Unknown') {
                throw new Error("User not authenticated");
            }
            
            const customerData = {
                action: 'add',
                customer_name: submitData.customer_name,
                store_name: submitData.store_name,
                phone: submitData.phone,
                email: submitData.email,
                date: submitData.date,
                address: submitData.address,
                category: submitData.category,
                customer_of: submitData.customer_of || null,
                username: loggedInUsername,
                shop_id: currentShop.shop_id,
                submission_id: submissionId,
                submission_timestamp: Date.now()
            };
            
            console.log('📤 Submitting customer data:', customerData);
            
            const response = await fetch('https://ourzestapp.online/oz_backend/customers.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(customerData)
            });

            const data = await response.json();
            console.log('📥 Customer API Response:', data);

            if (data.status === 'success') {
                showToast('success', translations.customerAdded || 'Customer saved successfully!');
                
                if (data.customer_login && data.customer_login.created) {
                    showToast('info', `Customer login created: ${data.customer_login.username} with password: Customer@123`);
                }
                
                // Clear form after successful submission
                setFormData(prev => ({
                    ...prev,
                    customer_name: '',
                    store_name: '',
                    phone: '',
                    email: '',
                    date: new Date().toISOString().split('T')[0],
                    address: '',
                    category: '',
                    customer_of: '',
                    themeColor: prev.themeColor
                }));
                setCategorySearchTerm('');
                setEmployeeSearchTerm('');
                setErrors({});
                
                if (formRef.current) {
                    formRef.current.reset();
                }
                
                return true;
            } else {
                if (data.duplicate_prevention || data.duplicate_database) {
                    showToast('warning', data.message || 'Duplicate submission detected');
                } else {
                    showToast('error', data.message || translations.failedAdd || 'Failed to save customer');
                }
                return false;
            }
        } catch (error) {
            console.error('❌ Error adding customer:', error);
            showToast('error', error.message || translations.networkError || 'Network error occurred');
            return false;
        } finally {
            // Release locks after delay (prevent rapid resubmission)
            setTimeout(() => {
                isSubmittingRef.current = false;
                submissionLockRef.current = false;
                setLoading(false);
                
                // Re-enable submit button
                if (submitButtonRef.current) {
                    submitButtonRef.current.disabled = false;
                }
            }, 2000);
        }
    }, [currentShop, user, translations, showToast]);

    // Debounced submit function with client-side duplicate prevention
    const debouncedSubmit = useCallback(async (submitData) => {
        // Check if already submitting
        if (isSubmittingRef.current || submissionLockRef.current) {
            console.log('🚫 Submission blocked - already in progress');
            showToast('warning', 'Please wait, submission in progress...');
            return;
        }

        const now = Date.now();
        const submissionId = generateSubmissionId();
        
        // Check for duplicate submission within 5 seconds
        if (lastSubmissionRef.current) {
            const timeDiff = now - lastSubmissionRef.current.time;
            const dataMatch = JSON.stringify(submitData) === lastSubmissionRef.current.data;
            
            if (timeDiff < 5000 && dataMatch) {
                console.log('🚫 Duplicate submission prevented (client-side)');
                showToast('warning', 'Please wait a moment before submitting again');
                return;
            }
        }

        // Store this submission
        lastSubmissionRef.current = {
            time: now,
            data: JSON.stringify(submitData),
            id: submissionId
        };

        setLoading(true);
        
        // Call the actual submit function
        await submitCustomer(submitData, submissionId);
        
    }, [submitCustomer, showToast]);

    // Handle form submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        console.log('🔔 Form submit triggered at:', new Date().toISOString());

        // Prevent default form submission behavior
        if (e.nativeEvent) {
            e.nativeEvent.preventDefault();
            e.nativeEvent.stopImmediatePropagation();
        }

        // Check if already submitting
        if (isSubmittingRef.current || submissionLockRef.current) {
            console.log('🚫 Submit prevented - already processing');
            showToast('warning', 'Please wait, already processing...');
            return;
        }

        if (!currentShop) {
            showToast('error', translations.selectShop || 'Please select a shop first');
            setShowShopSelector(true);
            return;
        }
        
        if (!validateForm()) {
            return;
        }
        
        // Call debounced submit
        await debouncedSubmit(formData);
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.customer_name.trim()) {
            newErrors.customer_name = translations.nameRequired || 'Customer name is required';
        } else if (formData.customer_name.trim().length < 2) {
            newErrors.customer_name = translations.nameTooShort || 'Name must be at least 2 characters';
        }
        
        if (formData.phone && !/^[\d\s\-\+\(\)]{10,15}$/.test(formData.phone)) {
            newErrors.phone = translations.phoneInvalid || 'Please enter a valid phone number';
        }
        
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = translations.emailInvalid || 'Please enter a valid email address';
        }
        
        if (formData.date && !/^\d{4}-\d{2}-\d{2}$/.test(formData.date)) {
            newErrors.date = translations.dateInvalid || 'Please select a valid date';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Handle category selection
    const selectCategory = (category) => {
        setFormData(prev => ({
            ...prev,
            category: category.value
        }));
        setCategorySearchTerm(category.displayName);
        setShowCategoryDropdown(false);
    };

    // Handle employee selection
    const selectEmployee = (employee) => {
        console.log('Selected employee:', employee);
        setFormData(prev => ({
            ...prev,
            customer_of: employee.employeeid
        }));
        setEmployeeSearchTerm(employee.employee_name);
        setShowEmployeeDropdown(false);
    };

    const handleClear = () => {
        // Only allow clear if not submitting
        if (isSubmittingRef.current || submissionLockRef.current) {
            showToast('warning', 'Please wait for current submission to complete');
            return;
        }
        
        setFormData(prev => ({
            ...prev,
            customer_name: '',
            store_name: '',
            phone: '',
            email: '',
            date: new Date().toISOString().split('T')[0],
            address: '',
            category: '',
            customer_of: '',
            themeColor: prev.themeColor
        }));
        setCategorySearchTerm('');
        setEmployeeSearchTerm('');
        setErrors({});
        lastSubmissionRef.current = null;
    };

    const handleSwitchShop = async (shop) => {
        // Don't allow shop switch during submission
        if (isSubmittingRef.current || submissionLockRef.current) {
            showToast('warning', 'Please wait for current submission to complete');
            return;
        }
        
        try {
            const success = await switchShop(
                shop.shop_id, 
                shop.shop_name, 
                shop.shop_code
            );
            
            if (success) {
                showToast('success', translations.shopSwitched || 'Shop switched successfully');
            } else {
                showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
        }
    };

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#6366f1';
                
                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                const palette = {
                    'customer-primary-color': color,
                    'customer-primary-dark': adjust(-40),
                    'customer-primary-light': adjust(40),
                    'customer-primary-ultralight': adjust(20),
                    'customer-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'customer-primary-shadow': `${color}20`
                };

                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });
            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(formData.themeColor);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#6366f1';
            if (theme !== formData.themeColor) {
                setFormData(prev => ({ ...prev, themeColor: theme }));
                applyTheme(theme);
            }
        };

        handleThemeChange();
        window.addEventListener('storage', handleThemeChange);
        const interval = setInterval(handleThemeChange, 1000);

        return () => {
            window.removeEventListener('storage', handleThemeChange);
            clearInterval(interval);
        };
    }, [formData.themeColor]);

    // Load translations
    useEffect(() => {
        const loadTranslations = async () => {
            const trans = await customerTranslator.getTranslations(currentLanguage);
            setTranslations(trans);
        };
        loadTranslations();
    }, [currentLanguage]);

    // Listen for language changes
    useEffect(() => {
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
            }
        };

        handleLanguageChange();
        window.addEventListener('storage', handleLanguageChange);
        const interval = setInterval(handleLanguageChange, 1000);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // Apply RTL direction
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        
        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
            document.documentElement.style.direction = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
        }
    }, [currentLanguage]);

    // Load available shops
    useEffect(() => {
        const loadAvailableShops = async () => {
            if (!user) return;
            
            setLoadingShops(true);
            try {
                const shopsData = await getShopsForSwitch();
                setAvailableShops(shopsData.shops || []);
            } catch (error) {
                console.error('Error loading shops:', error);
                setAvailableShops([]);
            } finally {
                setLoadingShops(false);
            }
        };

        loadAvailableShops();
    }, [user, getShopsForSwitch]);

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    // Render shop indicator
    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="shop-indicator-modern no-shop">
                    <div className="shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="shop-warning">
                            {translations.noShopSelected || 'No shop selected'}
                        </span>
                    </div>
                    <button 
                        className="btn-modern btn-switch-shop"
                        onClick={() => setShowShopSelector(true)}
                        disabled={isSubmittingRef.current || submissionLockRef.current}
                    >
                        <FontAwesomeIcon icon={faExchangeAlt} />
                        <span>{translations.switchShop || 'Switch Shop'}</span>
                    </button>
                </div>
            );
        }
        return null;
    };

    return (
        <div className={`customer-form-independent ${isRTL ? 'rtl' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification 
                toast={toast}
                setToast={setToast}
                translations={translations}
            />

            {/* Shop Selector Modal */}
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            {/* Modern Form Header */}
            <div className="form-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faUserPlus} />
                    </div>
                    <div className="header-text">
                        <h1>{translations.addCustomer || 'Save Customer'}</h1>
                        <p>{translations.fillDetails || 'Fill in the customer details below'}</p>
                    </div>
                </div>
                
                {/* Shop Indicator */}
                {renderShopIndicator()}
            </div>

            {/* Modern Form */}
            <form 
                ref={formRef}
                onSubmit={handleSubmit} 
                className="modern-form"
                noValidate
            >
                <div className="form-grid-modern">
                    {/* Customer Name Field */}
                    <div className={`form-group-modern ${errors.customer_name ? 'has-error' : ''}`}>
                        <div className="input-container">
                            <FontAwesomeIcon icon={faUser} className="input-icon" />
                            <input
                                type="text"
                                id="customer_name"
                                name="customer_name"
                                value={formData.customer_name}
                                onChange={handleChange}
                                required
                                placeholder={translations.customerName || 'Enter customer name'}
                                disabled={loading || isSubmittingRef.current}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                            <label htmlFor="customer_name" className="input-label">
                                {translations.customerName || 'Customer Name'}
                                <span className="required">*</span>
                            </label>
                        </div>
                        {errors.customer_name && (
                            <div className="error-message-modern">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                {errors.customer_name}
                            </div>
                        )}
                    </div>

                    {/* Store Name Field */}
                    <div className="form-group-modern">
                        <div className="input-container">
                            <FontAwesomeIcon icon={faStore} className="input-icon" />
                            <input
                                type="text"
                                id="store_name"
                                name="store_name"
                                value={formData.store_name}
                                onChange={handleChange}
                                placeholder={translations.storeName || 'Enter store name'}
                                disabled={loading || isSubmittingRef.current}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                            <label htmlFor="store_name" className="input-label">
                                {translations.storeName || 'Store Name'}
                                <span className="optional">{translations.optional || ' Optional'}</span>
                            </label>
                        </div>
                    </div>

                    {/* Phone Number Field */}
                    <div className={`form-group-modern ${errors.phone ? 'has-error' : ''}`}>
                        <div className="input-container">
                            <FontAwesomeIcon icon={faPhone} className="input-icon" />
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder={translations.phoneNumber || 'Enter phone number'}
                                disabled={loading || isSubmittingRef.current}
                                dir="ltr"
                            />
                            <label htmlFor="phone" className="input-label">
                                {translations.phoneNumber || 'Phone Number'}
                                <span className="optional">{translations.optional || ' Optional'}</span>
                            </label>
                        </div>
                        {errors.phone && (
                            <div className="error-message-modern">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                {errors.phone}
                            </div>
                        )}
                    </div>

                    {/* Email Field */}
                    <div className={`form-group-modern ${errors.email ? 'has-error' : ''}`}>
                        <div className="input-container">
                            <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
                            <input
                                type="text"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder={translations.email || 'Enter email address'}
                                disabled={loading || isSubmittingRef.current}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                            <label htmlFor="email" className="input-label">
                                {translations.email || 'Email'}
                                <span className="optional">{translations.optional || ' Optional'}</span>
                            </label>
                        </div>
                        {errors.email && (
                            <div className="error-message-modern">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                {errors.email}
                            </div>
                        )}
                    </div>

                    {/* Category Field */}
                    <div className="form-group-modern" ref={categoryDropdownRef}>
                        <div className="input-container">
                            <FontAwesomeIcon icon={faTag} className="input-icon" />
                            <input
                                type="text"
                                id="category_search"
                                value={categorySearchTerm}
                                onChange={(e) => {
                                    setCategorySearchTerm(e.target.value);
                                    setShowCategoryDropdown(true);
                                    if (formData.category && 
                                        !categoryOptions.find(c => 
                                            c.displayName.toLowerCase() === e.target.value.toLowerCase()
                                        )) {
                                        setFormData(prev => ({ ...prev, category: '' }));
                                    }
                                }}
                                onFocus={() => setShowCategoryDropdown(true)}
                                placeholder={translations.selectCategory || 'Select a category'}
                                disabled={loading || isSubmittingRef.current}
                                dir={isRTL ? 'rtl' : 'ltr'}
                                autoComplete="off"
                            />
                            <label htmlFor="category_search" className="input-label">
                                {translations.category || 'Category'}
                                <span className="optional">{translations.optional || ' Optional'}</span>
                            </label>
                            <FontAwesomeIcon icon={faChevronDown} className="dropdown-icon" />
                        </div>

                        {/* Category Dropdown */}
                        {showCategoryDropdown && (
                            <div className="category-dropdown">
                                {filteredCategories.length > 0 ? (
                                    filteredCategories.map(category => (
                                        <div
                                            key={category.value}
                                            className={`category-option ${formData.category === category.value ? 'selected' : ''}`}
                                            onClick={() => selectCategory(category)}
                                        >
                                            <div className="category-name">
                                                {translations[category.label] || category.displayName}
                                            </div>
                                            <div className="category-value">
                                                {category.value}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="category-dropdown empty">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        No categories found
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Customer Of Field - Employee Dropdown */}
                    <div className="form-group-modern" ref={employeeDropdownRef}>
                        <div className="input-container">
                            <FontAwesomeIcon icon={faUsers} className="input-icon" />
                            <input
                                type="text"
                                id="employee_search"
                                value={employeeSearchTerm}
                                onChange={(e) => {
                                    setEmployeeSearchTerm(e.target.value);
                                    setShowEmployeeDropdown(true);
                                    if (formData.customer_of && 
                                        !employees.find(emp => emp.employee_name === e.target.value)) {
                                        setFormData(prev => ({ ...prev, customer_of: '' }));
                                    }
                                }}
                                onFocus={() => setShowEmployeeDropdown(true)}
                                placeholder={translations.searchEmployee || 'Search employee...'}
                                disabled={loading || isSubmittingRef.current || loadingEmployees}
                                dir={isRTL ? 'rtl' : 'ltr'}
                                autoComplete="off"
                            />
                            <label htmlFor="employee_search" className="input-label">
                                <FontAwesomeIcon icon={faUser} style={{ marginRight: '5px' }} />
                                {translations.customerOf || 'Customer Of'}
                                <span className="optional">{translations.optional || ' Optional'}</span>
                            </label>
                            <FontAwesomeIcon icon={faChevronDown} className="dropdown-icon" />
                        </div>

                        {/* Employee Dropdown */}
                        {showEmployeeDropdown && (
                            <div className="category-dropdown">
                                {loadingEmployees ? (
                                    <div className="category-dropdown loading">
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                        {translations.loadingEmployees || 'Loading employees...'}
                                    </div>
                                ) : filteredEmployees.length > 0 ? (
                                    filteredEmployees.map(employee => (
                                        <div
                                            key={employee.employeeid}
                                            className={`category-option ${formData.customer_of === employee.employeeid ? 'selected' : ''}`}
                                            onClick={() => selectEmployee(employee)}
                                        >
                                            <div className="category-name">
                                                {employee.employee_name}
                                            </div>
                                            <div className="category-value">
                                                {employee.position || 'Employee'} 
                                                {employee.department ? ` - ${employee.department}` : ''}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="category-dropdown empty">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        {employeeSearchTerm 
                                            ? (translations.noMatchingEmployees || 'No matching employees found')
                                            : (translations.noEmployees || 'No employees available for this shop')}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Date Field */}
                    <div className={`form-group-modern ${errors.date ? 'has-error' : ''}`}>
                        <div className="input-container">
                            <FontAwesomeIcon icon={faCalendar} className="input-icon" />
                            <input
                                type="date"
                                id="date"
                                name="date"
                                value={formData.date}
                                onChange={handleChange}
                                disabled={loading || isSubmittingRef.current}
                                dir="ltr"
                            />
                            <label htmlFor="date" className="input-label">
                                {translations.date || 'Date'}
                            </label>
                        </div>
                        {errors.date && (
                            <div className="error-message-modern">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                {errors.date}
                            </div>
                        )}
                    </div>
                </div>

                {/* Address Field - Full Width */}
                <div className="form-group-modern">
                    <div className="input-container">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="input-icon" />
                        <textarea
                            id="address"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            rows="3"
                            placeholder={translations.address || 'Enter customer address'}
                            disabled={loading || isSubmittingRef.current}
                            dir={isRTL ? 'rtl' : 'ltr'}
                        ></textarea>
                        <label htmlFor="address" className="input-label">
                            {translations.address || 'Address'}
                            <span className="optional">{translations.optional || ' Optional'}</span>
                        </label>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="form-actions-modern">
                    <button
                        type="button"
                        className="btn-modern btn-secondary-modern"
                        onClick={handleClear}
                        disabled={loading || isSubmittingRef.current || submissionLockRef.current}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                        <span>{translations.clearButton || 'Clear'}</span>
                    </button>
                    <button
                        ref={submitButtonRef}
                        type="submit"
                        className="btn-modern btn-primary-modern"
                        disabled={loading || !currentShop || isSubmittingRef.current || submissionLockRef.current}
                    >
                        {loading || isSubmittingRef.current ? (
                            <>
                                <div className="btn-spinner"></div>
                                <span>{translations.adding || 'Adding...'}</span>
                            </>
                        ) : (
                            <>
                                <span>{translations.addCustomerButton || 'Save Customer'}</span>
                                <FontAwesomeIcon icon={faSave} />
                            </>
                        )}
                    </button>
                </div>
                
                {/* Submission Status Indicator */}
                {(isSubmittingRef.current || submissionLockRef.current) && (
                    <div className="submission-status">
                        <div className="submission-status-text">
                            <FontAwesomeIcon icon={faSpinner} spin />
                            <span>Processing your submission...</span>
                        </div>
                    </div>
                )}
            </form>

            {/* Loading Overlay */}
            {loading && (
                <div className="loading-overlay">
                    <div className="loading-content">
                        <div className="loading-spinner"></div>
                        <p>{translations.adding || 'Adding...'}</p>
                        <p className="loading-subtext">Please wait while we save customer data</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddCustomer;